import React, { useState } from "react"
import containerClasses from "../../styles/container.module.css"
import cadoganClinic from "../../assets/images/clinic-door-x2.webp"
import lauristonClinic from "../../assets/images/London-Lauriston.jpg"

// Walking-person icon used inline next to the "X min walk from Y station"
// lines. Inline so there's no asset-import path to break.
const WalkingIcon = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9 7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7" />
  </svg>
)

// Booking line shown in every location panel.
const BookingLine = () => (
  <p className="mt-5 mb-0 text-base text-neutral-500">
    To book, please use{" "}
    <a href="/contact/" className="text-gold600 text-base">
      our contact form
    </a>{" "}
    or call{" "}
    <a href="tel:+447702767200" className="text-gold600 text-base">
      07702 767 200
    </a>
  </p>
)

type LocationKey = "cadogan" | "lauriston"

type ConsultingLocationsProps = {
  overline?: string
  heading?: string
  introCopy?: string
}

export const ConsultingLocations = ({
  overline = "Consulting Locations",
  heading,
  introCopy,
}: ConsultingLocationsProps) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationKey>("cadogan")

  return (
    <div className="py-20 md:py-28 lg:py-32 xl:py-40 bg-white2">
      <div className={`${containerClasses.container}`}>
        {/* Centered header + pills */}
        <div className="col-span-4 md:col-span-8 lg:col-span-12 text-center mb-10">
          <p className="mt-0 overline">{overline}</p>
          <h2
            className={`mt-2 fluid-heading-05 max-w-xl mx-auto ${
              introCopy ? "mb-4" : "mb-8"
            }`}
          >
            {heading || "Our London locations"}
          </h2>
          {introCopy && (
            <p className="max-w-xl mx-auto mt-0 mb-8 text-neutral-500 leading-relaxed text-center">
              {introCopy}
            </p>
          )}
          <div
            role="tablist"
            aria-label="Consulting locations"
            className="inline-flex gap-2 p-1 bg-gray-100 rounded-full"
          >
            <button
              role="tab"
              id="cadogan-tab"
              aria-controls="cadogan-panel"
              aria-selected={selectedLocation === "cadogan"}
              onClick={() => setSelectedLocation("cadogan")}
              className={`font-mono text-xs tracking-wider uppercase px-5 py-2 rounded-full transition-all duration-200 ${
                selectedLocation === "cadogan"
                  ? "bg-blackNew text-white"
                  : "text-neutral-500 hover:text-blackNew"
              }`}
            >
              Cadogan Clinic
            </button>
            <button
              role="tab"
              id="lauriston-tab"
              aria-controls="lauriston-panel"
              aria-selected={selectedLocation === "lauriston"}
              onClick={() => setSelectedLocation("lauriston")}
              className={`font-mono text-xs tracking-wider uppercase px-5 py-2 rounded-full transition-all duration-200 ${
                selectedLocation === "lauriston"
                  ? "bg-blackNew text-white"
                  : "text-neutral-500 hover:text-blackNew"
              }`}
            >
              The London Lauriston Clinic
            </button>
          </div>
        </div>

        {/* Cadogan panel — always rendered, hidden when not selected */}
        <div
          role="tabpanel"
          id="cadogan-panel"
          aria-labelledby="cadogan-tab"
          hidden={selectedLocation !== "cadogan"}
          className="col-span-4 md:col-span-8 lg:col-span-12 grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-4 md:gap-6"
        >
          {/* Image */}
          <div className="col-span-4 md:col-span-4 lg:col-span-6">
            <div
              className="relative w-full bg-gray-100 overflow-hidden"
              style={{ aspectRatio: "4/3" }}
            >
              <img
                src={cadoganClinic}
                alt="The Cadogan Clinic — 120 Sloane Street, Chelsea, London"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div className="col-span-4 md:col-span-4 lg:col-span-5 lg:col-start-8 flex flex-col justify-center mt-8 lg:mt-0">
            <p className="mt-0 mb-2 font-mono text-sm tracking-wider uppercase text-gold600">
              Chelsea
            </p>
            <h3 className="mt-0 mb-5 fluid-heading-04">Cadogan Clinic</h3>
            <p className="mb-0 text-lg text-neutral-500 leading-relaxed">
              120 Sloane Street
              <br />
              Chelsea, London
              <br />
              SW1X 9BW
            </p>
            <p className="mt-5 mb-0 text-base text-neutral-500 flex items-center gap-2">
              <WalkingIcon className="h-5 w-5 flex-shrink-0" />
              <span>5 min walk from Sloane Square station</span>
            </p>
            <BookingLine />
          </div>
        </div>

        {/* Lauriston panel — always rendered, hidden when not selected */}
        <div
          role="tabpanel"
          id="lauriston-panel"
          aria-labelledby="lauriston-tab"
          hidden={selectedLocation !== "lauriston"}
          className="col-span-4 md:col-span-8 lg:col-span-12 grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 gap-4 md:gap-6"
        >
          {/* Image */}
          <div className="col-span-4 md:col-span-4 lg:col-span-6">
            <div
              className="relative w-full bg-gray-100 overflow-hidden"
              style={{ aspectRatio: "4/3" }}
            >
              <img
                src={lauristonClinic}
                alt="The London Lauriston Clinic — 34 Great Titchfield Street, central London"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Details */}
          <div className="col-span-4 md:col-span-4 lg:col-span-5 lg:col-start-8 flex flex-col justify-center mt-8 lg:mt-0">
            <p className="mt-0 mb-2 font-mono text-sm tracking-wider uppercase text-gold600">
              Central London
            </p>
            <h3 className="mt-0 mb-5 fluid-heading-04">
              The London Lauriston Clinic
            </h3>
            <p className="mb-0 text-lg text-neutral-500 leading-relaxed">
              34 Great Titchfield Street
              <br />
              Central London
              <br />
              W1W 8BQ
            </p>
            <p className="mt-5 mb-0 text-base text-neutral-500 flex items-center gap-2">
              <WalkingIcon className="h-5 w-5 flex-shrink-0" />
              <span>5 min walk from Oxford Circus station</span>
            </p>
            <BookingLine />
          </div>
        </div>
      </div>
    </div>
  )
}
